import classNames from "classnames";
import React from "react";
import KuikaSvg from "../kuika-svg/kuika-svg";
import "./kuika-field.scss";

export interface KuikaFieldCounter {
  current: number;
  max: number;
}

export interface KuikaFieldProps {
  label: React.ReactNode;
  /** The id of the control the label belongs to. */
  htmlFor?: string;
  /** Marks the field "(optional)" after the label. */
  optional?: boolean;
  /** Hint under the control; replaced by `error` when there is one. */
  help?: React.ReactNode;
  error?: string;
  /** A "current/max" character counter at the end of the label row. */
  counter?: KuikaFieldCounter;
  className?: string;
  children: React.ReactNode;
}

/**
 * A labelled form control outside an antd `Form`: label, optional marker, counter, help text and
 * validation error. Fields driven by an antd form store use `KuikaFormField` from `kuika-form-page`;
 * the two keep separate class blocks (`kuika-field` / `kuika-form-field`) so neither restyles the other.
 */
const KuikaField = ({
  label,
  htmlFor,
  optional = false,
  help,
  error,
  counter,
  className,
  children
}: KuikaFieldProps): React.ReactElement => {
  const messageId: string | undefined = htmlFor ? `${htmlFor}-message` : undefined;

  return (
    <div className={classNames("kuika-field", className, { "kuika-field--error": !!error })}>
      <div className="kuika-field__header">
        <label className="kuika-field__label" htmlFor={htmlFor}>
          {label}
          {optional && <span className="kuika-field__optional">(optional)</span>}
        </label>
        {counter && (
          <span
            className={classNames("kuika-field__counter", {
              "kuika-field__counter--over": counter.current > counter.max
            })}
            aria-live="polite"
          >
            {counter.current}/{counter.max}
          </span>
        )}
      </div>
      {children}
      {error ? (
        <div className="kuika-field__error" id={messageId} role="alert">
          <KuikaSvg name="info-circle" width={12} height={12} color="var(--red-5)" />
          <span>{error}</span>
        </div>
      ) : (
        help && (
          <div className="kuika-field__help" id={messageId}>
            {help}
          </div>
        )
      )}
    </div>
  );
};

export default KuikaField;
